import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, MessageCircle, ShoppingBag, Star, Zap } from "lucide-react";
import api, { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import SEO from "@/components/SEO";

export default function EventExperienceDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { addItem } = useCart();
    const [item, setItem] = useState(null);
    const [related, setRelated] = useState([]);
    const [notFound, setNotFound] = useState(false);
    const isEventPackage = item?.is_event_package ?? /event|wedding|corporate|exhibition|catering|farmhouse|mice|conference|launch|promotion|gifting|celebration|venue|production|experience|activation/i.test(`${item?.title || ""} ${item?.category || ""} ${(item?.tags || []).join(" ")}`);

    useEffect(() => {
        let mounted = true;
        api.get(`/catalog/products/${slug}`).then(({ data }) => {
            if (!mounted) return;
            if (data.product?.demo_catalog) {
                setNotFound(true);
                return;
            }
            setItem(data.product);
            setRelated((data.related || []).filter((recommendation) => !recommendation.demo_catalog));
        }).catch(() => { if (mounted) setNotFound(true); });
        return () => { mounted = false; };
    }, [slug]);

    useEffect(() => {
        if (notFound) navigate("/products", { replace: true });
    }, [notFound, navigate]);

    if (!item) return <div className="xp-detail-loading">Loading product…</div>;

    const availableVariant = (item.variants || []).find((variant) => variant.stock - (variant.reserved || 0) > 0);
    const purchasable = item.price > 0 && availableVariant;
    const buyNow = () => {
        addItem(item.id, availableVariant.sku, 1, { openDrawer: false });
        navigate("/checkout");
    };
    const allItemsPath = isEventPackage ? "/experiences" : "/products";
    const relatedTitle = isEventPackage ? "packages" : "products";

    return (
        <div className="xp-detail-page">
            <SEO title={item.title} description={(item.description || "An X-Plosiv product or event experience.").slice(0, 160)} image={item.images?.[0]} />
            <div className="xp-detail-wrap">
                <Link to={allItemsPath} className="xp-detail-back"><ArrowLeft size={15} /> All {isEventPackage ? "experiences" : "products"}</Link>
                <div className="xp-detail-grid">
                    <div className="xp-detail-media">
                        <img src={item.images?.[0]} alt={item.title} />
                        {item.images?.slice(1, 4).map((image) => <img key={image} src={image} alt="" />)}
                    </div>
                    <div className="xp-detail-copy">
                        <p className="xp-detail-eyebrow">{isEventPackage ? item.category || "X-PLOSIV EVENT EXPERIENCE" : item.category || "X-PLOSIV PRODUCT"}</p>
                        <h1>{item.title}</h1>
                        <div className="xp-detail-rating"><Star size={15} fill="currentColor" /><strong>{Number(item.avg_rating || 4.8).toFixed(1)}</strong><span>({item.review_count || 0} reviews)</span></div>
                        <p>{item.description || "A tailored X-Plosiv experience, thoughtfully planned around your brief."}</p>
                        <div className="xp-detail-points">
                            {(item.tags || ["Creative direction", "Event planning", "Production"]).slice(0, 5).map((tag) => <span key={tag}><Check size={15} />{tag}</span>)}
                        </div>
                        {purchasable ? (
                            <>
                                <p className="xp-detail-price">{formatPrice(item.price)} <small>{isEventPackage ? "per package" : ""}</small></p>
                                <p className="xp-detail-availability">{availableVariant.stock - (availableVariant.reserved || 0)} {isEventPackage ? "booking places" : "in stock"}</p>
                                <div className="xp-detail-purchase">
                                    <button className="xp-detail-button xp-detail-button--bag" onClick={() => addItem(item.id, availableVariant.sku)}><ShoppingBag size={16} /> Add to bag</button>
                                    <button className="xp-detail-button xp-detail-button--gold" onClick={buyNow}><Zap size={16} /> Buy now</button>
                                </div>
                            </>
                        ) : (
                            <div className="xp-detail-note"><MessageCircle size={19} /><span><strong>{isEventPackage ? "Every event is unique." : "Currently unavailable."}</strong><small>{isEventPackage ? "Request a tailored proposal around your date, location and guest list." : "Contact our team for help with this product."}</small></span></div>
                        )}
                        <Link className={`xp-detail-button xp-detail-button--contact${purchasable ? " xp-detail-button--outline" : ""}`} to={`/contact?service=${encodeURIComponent(item.title)}`}>
                            {isEventPackage ? "Enquire about this experience" : "Ask us about this product"} <ArrowRight size={17} />
                        </Link>
                        <p className="xp-detail-contact">Prefer a quick chat? <a href="https://wa.me/919810525439" target="_blank" rel="noreferrer">Message us on WhatsApp</a></p>
                    </div>
                </div>
                {related.length > 0 && (
                    <section className="xp-related" aria-labelledby="xp-related-heading">
                        <div className="xp-related__heading">
                            <div><p>MORE TO EXPLORE</p><h2 id="xp-related-heading">Related {relatedTitle}</h2></div>
                            <Link to={allItemsPath}>See all <ArrowRight size={15} /></Link>
                        </div>
                        <div className="xp-related__grid">
                            {related.slice(0, 4).map((product) => (
                                <Link className="xp-related-card" key={product.id} to={`/product/${product.slug}`}>
                                    <img src={product.images?.[0]} alt={product.title} loading="lazy" />
                                    <span>{product.category}</span>
                                    <strong>{product.title}</strong>
                                    <small>{formatPrice(product.price)}</small>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
